import PropTypes from 'prop-types'
import { useMemo } from 'react'
import { ArrowRight, GitBranch, ArrowUpRight } from 'lucide-react'
import { STATUS_COLORS, DIFFICULTY_LABELS } from './knowledgeMapUtils'

/**
 * Dependencies view shared by the educator and student knowledge-map node detail panels.
 *
 * Lists the node's prerequisites (what it depends on) and dependents (what depends on it), resolved
 * from the already-loaded `allNodes` list by `skillGroupKey`. Each row is clickable and calls
 * `onNavigate(skillGroupKey)` so the parent panel can re-target that node — the same click-to-navigate
 * pattern as the admin node modal's Relationships tab.
 *
 * @param {Object} node - the selected node (must have `skillGroupKey` and `prerequisites`)
 * @param {Array}  allNodes - every node currently loaded for this test type
 * @param {Function} onNavigate - (skillGroupKey) => void, selects that node
 */
export default function NodeDependencies({ node, allNodes = [], onNavigate }) {
  // Resolve prerequisites/dependents from allNodes; memoize so the Map rebuild + filter don't run on
  // every render (allNodes can be the full node set for the test type).
  const byKey = useMemo(() => new Map(allNodes.map((n) => [n.skillGroupKey, n])), [allNodes])

  const prerequisites = useMemo(
    () =>
      (node?.prerequisites || []).map(
        (key) => byKey.get(key) || { skillGroupKey: key, skill: key }
      ),
    [node?.prerequisites, byKey]
  )
  const dependents = useMemo(
    () => allNodes.filter((n) => (n.prerequisites || []).includes(node?.skillGroupKey)),
    [allNodes, node?.skillGroupKey]
  )

  return (
    <div className="space-y-5">
      <DependencySection
        title="Prerequisites"
        icon={<GitBranch className="w-4 h-4" />}
        items={prerequisites}
        emptyText="No prerequisites (foundational node)"
        onNavigate={onNavigate}
      />
      <DependencySection
        title="Dependents"
        icon={<ArrowUpRight className="w-4 h-4" />}
        items={dependents}
        emptyText="No nodes depend on this"
        onNavigate={onNavigate}
      />
    </div>
  )
}

function DependencySection({ title, icon, items, emptyText, onNavigate }) {
  return (
    <section>
      <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-1.5">
        {icon}
        {title} ({items.length})
      </h3>
      {items.length > 0 ? (
        <div className="space-y-2">
          {items.map((item) => {
            const status = item.mastery?.status || 'UNVISITED'
            const colors = STATUS_COLORS[status] || STATUS_COLORS.UNVISITED
            const diffLabel = DIFFICULTY_LABELS[item.difficulty] || item.difficulty
            return (
              <button
                key={item.skillGroupKey}
                onClick={() => onNavigate?.(item.skillGroupKey)}
                className="w-full flex items-center justify-between gap-2 p-2 rounded-lg bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-left"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span
                      className="w-2 h-2 rounded-full flex-shrink-0"
                      style={{ backgroundColor: colors.dot }}
                      title={colors.text}
                    />
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                      {item.skill}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 ml-3.5">
                    {[item.domain, diffLabel].filter(Boolean).join(' · ')}
                  </div>
                </div>
                <ArrowRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
              </button>
            )
          })}
        </div>
      ) : (
        <p className="text-sm text-gray-500 dark:text-gray-400 italic">{emptyText}</p>
      )}
    </section>
  )
}

const itemShape = PropTypes.shape({
  skillGroupKey: PropTypes.string,
  skill: PropTypes.string,
  domain: PropTypes.string,
  difficulty: PropTypes.string,
  mastery: PropTypes.object
})

DependencySection.propTypes = {
  title: PropTypes.string.isRequired,
  icon: PropTypes.node,
  items: PropTypes.arrayOf(itemShape).isRequired,
  emptyText: PropTypes.string.isRequired,
  onNavigate: PropTypes.func
}

NodeDependencies.propTypes = {
  node: PropTypes.shape({
    skillGroupKey: PropTypes.string,
    prerequisites: PropTypes.arrayOf(PropTypes.string)
  }),
  allNodes: PropTypes.array,
  onNavigate: PropTypes.func
}
